import React, { useState } from 'react';
import { Permission, PermissionMetadata, getPermissionCategories, getPermissionsByCategory, PermissionGroups } from '../types/permissions.ts';
import { Check, X, Info, Shield, Users, AlertTriangle } from 'lucide-react';

interface PermissionManagerProps {
  selectedPermissions: Permission[];
  onPermissionsChange: (permissions: Permission[]) => void;
  showPresets?: boolean;
  disabled?: boolean;
}

const PermissionManager: React.FC<PermissionManagerProps> = ({
  selectedPermissions,
  onPermissionsChange,
  showPresets = true,
  disabled = false,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(getPermissionCategories())
  );

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const togglePermission = (permission: Permission) => {
    if (disabled) return;

    const newPermissions = new Set(selectedPermissions);
    if (newPermissions.has(permission)) {
      newPermissions.delete(permission);
    } else {
      newPermissions.add(permission);
    }
    onPermissionsChange(Array.from(newPermissions));
  };

  const applyPreset = (permissions: Permission[]) => {
    if (disabled) return;
    onPermissionsChange([...permissions]);
  };

  const hasPermission = (permission: Permission) => {
    return selectedPermissions.includes(permission);
  };

  const getCategoryPermissionCount = (category: string) => {
    const categoryPermissions = getPermissionsByCategory(category);
    const selectedCount = categoryPermissions.filter(p => hasPermission(p)).length;
    return { selected: selectedCount, total: categoryPermissions.length };
  };

  const categories = getPermissionCategories();

  return (
    <div className="space-y-6">
      {/* Permission Presets */}
      {showPresets && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Permission Presets
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => applyPreset(PermissionGroups.ADMIN)}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <Shield className="w-4 h-4 mr-1" />
              Administrator
            </button>
            <button
              type="button"
              onClick={() => applyPreset(PermissionGroups.INVOICE_STAFF)}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-indigo-100 text-indigo-800 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Invoice Staff
            </button>
            <button
              type="button"
              onClick={() => applyPreset(PermissionGroups.TIMESHEET_STAFF)}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Timesheet Staff
            </button>
            <button
              type="button"
              onClick={() => applyPreset(PermissionGroups.FIELD_ELECTRICIAN)}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-green-100 text-green-800 rounded-md hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Field Electrician
            </button>
            <button
              type="button"
              onClick={() => applyPreset(PermissionGroups.CONTRACTOR)}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-purple-100 text-purple-800 rounded-md hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Contractor
            </button>
            <button
              type="button"
              onClick={() => applyPreset([])}
              disabled={disabled}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Permission Categories */}
      <div className="space-y-4">
        {categories.map((category) => {
          const categoryPermissions = getPermissionsByCategory(category);
          const { selected, total } = getCategoryPermissionCount(category);
          const isExpanded = expandedCategories.has(category);

          return (
            <div key={category} className="border border-gray-200 rounded-lg">
              {/* Category Header */}
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg flex items-center justify-between"
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-900">{category}</span>
                  <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                    {selected}/{total}
                  </span>
                </div>
                <div className="flex items-center">
                  {selected > 0 && (
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                  )}
                  <svg
                    className={`w-4 h-4 text-gray-500 transform transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Category Permissions */}
              {isExpanded && (
                <div className="px-4 py-3 space-y-3">
                  {categoryPermissions.map((permission) => {
                    const metadata = PermissionMetadata[permission];
                    const isSelected = hasPermission(permission);
                    const isDangerous = metadata.dangerous;

                    return (
                      <div
                        key={permission}
                        className={`flex items-start space-x-3 p-3 rounded-lg border ${
                          isSelected
                            ? isDangerous
                              ? 'bg-red-50 border-red-200'
                              : 'bg-blue-50 border-blue-200'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        onClick={() => togglePermission(permission)}
                      >
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? isDangerous
                                  ? 'bg-red-600 border-red-600'
                                  : 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <h5 className={`text-sm font-medium ${
                              isSelected ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {metadata.name}
                            </h5>
                            {isDangerous && (
                              <AlertTriangle className="w-4 h-4 text-red-500 ml-2 flex-shrink-0" />
                            )}
                          </div>
                          <p className={`text-sm mt-1 ${
                            isSelected ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {metadata.description}
                          </p>
                          {isDangerous && (
                            <p className="text-xs text-red-600 mt-1 flex items-center">
                              <Info className="w-3 h-3 mr-1" />
                              Potentially dangerous permission - use with caution
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Permission Summary */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-center mb-2">
          <Info className="w-4 h-4 text-blue-600 mr-2" />
          <h4 className="text-sm font-medium text-blue-900">Selected Permissions Summary</h4>
        </div>
        <p className="text-sm text-blue-800">
          {selectedPermissions.length === 0
            ? 'No permissions selected. User will have very limited access.'
            : selectedPermissions.includes(Permission.ADMIN)
            ? 'Administrator permissions selected. User will have full system access.'
            : `${selectedPermissions.length} permission${selectedPermissions.length !== 1 ? 's' : ''} selected.`}
        </p>
        {selectedPermissions.some(p => PermissionMetadata[p]?.dangerous) && (
          <p className="text-sm text-red-700 mt-2 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-1" />
            This user has been granted potentially dangerous permissions.
          </p>
        )}
      </div>
    </div>
  );
};

export default PermissionManager;